import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import modelsApi from '../../api/modelsApi';

const ModelsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [llmModels, setLlmModels] = useState([]);
  const [embeddingModels, setEmbeddingModels] = useState([]);
  const [isLlmLoading, setIsLlmLoading] = useState(true);
  const [isEmbeddingLoading, setIsEmbeddingLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOpenAI, setShowOpenAI] = useState(true);
  const [showOllama, setShowOllama] = useState(true);

  useEffect(() => {
    fetchLlmModels();
    fetchEmbeddingModels();
  }, []);

  const fetchLlmModels = async () => {
    try {
      setIsLlmLoading(true);
      const response = await modelsApi.listLlmModels();
      setLlmModels(response.models || []);
    } catch (error) {
      console.error('LLM 모델을 가져오는 중 오류 발생:', error);
      setError('LLM 모델 목록을 불러올 수 없습니다.');
    } finally {
      setIsLlmLoading(false);
    }
  };

  const fetchEmbeddingModels = async () => {
    try {
      setIsEmbeddingLoading(true);
      const response = await modelsApi.listEmbeddingModels();
      setEmbeddingModels(response.models || []);
    } catch (error) {
      console.error('임베딩 모델을 가져오는 중 오류 발생:', error);
      setError('임베딩 모델 목록을 불러올 수 없습니다.');
    } finally {
      setIsEmbeddingLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getProviderIcon = (provider) => {
    switch (provider.toLowerCase()) {
      case 'openai':
        return <SmartToyIcon />;
      case 'ollama':
        return <ModelTrainingIcon />;
      case 'sentence-transformers':
        return <TextSnippetIcon />;
      default:
        return <ModelTrainingIcon />;
    }
  };

  const getProviderColor = (provider) => {
    switch (provider.toLowerCase()) {
      case 'openai':
        return 'primary';
      case 'ollama':
        return 'secondary';
      case 'sentence-transformers':
        return 'success';
      default:
        return 'default';
    }
  };

  const filteredLlmModels = llmModels.filter(model => {
    if (!showOpenAI && model.provider.toLowerCase() === 'openai') return false;
    if (!showOllama && model.provider.toLowerCase() === 'ollama') return false;
    return true;
  });

  const filteredEmbeddingModels = embeddingModels.filter(model => {
    if (!showOpenAI && model.provider.toLowerCase() === 'openai') return false;
    if (!showOllama && model.provider.toLowerCase() === 'ollama') return false;
    return true;
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        모델 관리
      </Typography>
      
      <Paper elevation={3} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="LLM 모델" />
          <Tab label="임베딩 모델" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6">
                {activeTab === 0 ? 'LLM 모델 목록' : '임베딩 모델 목록'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {activeTab === 0 
                  ? '질의응답에 사용되는 대규모 언어 모델(LLM) 목록입니다.' 
                  : '문서 임베딩에 사용되는 벡터 임베딩 모델 목록입니다.'}
              </Typography>
            </Box>
            <Button
              startIcon={<RefreshIcon />}
              onClick={activeTab === 0 ? fetchLlmModels : fetchEmbeddingModels}
              disabled={activeTab === 0 ? isLlmLoading : isEmbeddingLoading}
            >
              새로고침
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showOpenAI}
                  onChange={(e) => setShowOpenAI(e.target.checked)}
                />
              }
              label="OpenAI"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showOllama}
                  onChange={(e) => setShowOllama(e.target.checked)}
                />
              }
              label="Ollama"
            />
          </Box>
          
          {error && <ErrorMessage message={error} sx={{ mb: 2 }} />}
          
          {activeTab === 0 ? (
            isLlmLoading ? (
              <LoadingSpinner message="LLM 모델 정보를 불러오는 중입니다..." />
            ) : filteredLlmModels.length === 0 ? (
              <Alert severity="info">
                <AlertTitle>표시할 모델 없음</AlertTitle>
                선택한 필터에 해당하는 LLM 모델이 없습니다.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {filteredLlmModels.map((model) => (
                  <Grid item xs={12} sm={6} md={4} key={model.id}>
                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getProviderIcon(model.provider)}
                          </ListItemIcon>
                          <Typography variant="h6" component="div" noWrap>
                            {model.name}
                          </Typography>
                        </Box>
                        
                        <Chip 
                          label={model.provider}
                          color={getProviderColor(model.provider)}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {model.description || '설명 없음'}
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: 160 }}>
                              최대 토큰 수:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {model.max_tokens ? model.max_tokens.toLocaleString() : '제한 없음'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: 160 }}>
                              스트리밍 지원:
                            </Typography>
                            {model.is_streaming_supported ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <CancelIcon color="error" fontSize="small" />
                            )}
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ minWidth: 160 }}>
                              비전 기능 지원:
                            </Typography>
                            {model.is_vision_supported ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <CancelIcon color="error" fontSize="small" />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                      
                      <CardActions>
                        <Button 
                          size="small" 
                          color="primary"
                          startIcon={<HelpOutlineIcon />}
                          href={
                            model.provider.toLowerCase() === 'openai' 
                              ? `https://platform.openai.com/docs/models/${model.id}` 
                              : model.provider.toLowerCase() === 'ollama'
                                ? `https://ollama.com/library/${model.id}`
                                : '#'
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          모델 정보
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )
          ) : (
            isEmbeddingLoading ? (
              <LoadingSpinner message="임베딩 모델 정보를 불러오는 중입니다..." />
            ) : filteredEmbeddingModels.length === 0 ? (
              <Alert severity="info">
                <AlertTitle>표시할 모델 없음</AlertTitle>
                선택한 필터에 해당하는 임베딩 모델이 없습니다.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {filteredEmbeddingModels.map((model) => (
                  <Grid item xs={12} sm={6} md={4} key={model.id}>
                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getProviderIcon(model.provider)}
                          </ListItemIcon>
                          <Typography variant="h6" component="div" noWrap>
                            {model.name}
                          </Typography>
                        </Box>
                        
                        <Chip 
                          label={model.provider}
                          color={getProviderColor(model.provider)}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {model.description || '설명 없음'}
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: 160 }}>
                              차원 수:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {model.dimensions || '-'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ minWidth: 160 }}>
                              컨텍스트 길이:
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {model.max_tokens ? model.max_tokens.toLocaleString() : '제한 없음'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                      
                      <CardActions>
                        <Button 
                          size="small" 
                          color="primary"
                          startIcon={<HelpOutlineIcon />}
                          href={
                            model.provider.toLowerCase() === 'openai' 
                              ? `https://platform.openai.com/docs/models/${model.id}` 
                              : model.provider.toLowerCase() === 'sentence-transformers'
                                ? `https://huggingface.co/sentence-transformers/${model.id.replace('sentence-transformers/', '')}`
                                : '#'
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          모델 정보
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )
          )}
        </Box>
      </Paper>
      
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          모델 사용 가이드
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              OpenAI 모델
            </Typography>
            <Typography variant="body2" paragraph>
              OpenAI 모델을 사용하려면 OpenAI API 키를 환경 변수(OPENAI_API_KEY)에 설정해야 합니다. 
              GPT-4o, GPT-3.5-turbo 등 다양한 모델을 지원합니다.
            </Typography>
            <Typography variant="body2">
              자세한 내용은 <a href="https://platform.openai.com/docs" target="_blank" rel="noopener noreferrer">OpenAI 공식 문서</a>를 참조하세요.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Ollama 모델
            </Typography>
            <Typography variant="body2" paragraph>
              Ollama 모델을 사용하려면 Ollama 서버가 실행 중이어야 합니다. 
              Ollama 서버 URL을 환경 변수(OLLAMA_BASE_URL)에 설정할 수 있습니다.
            </Typography>
            <Typography variant="body2">
              자세한 내용은 <a href="https://ollama.com/docs" target="_blank" rel="noopener noreferrer">Ollama 공식 문서</a>를 참조하세요.
            </Typography>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            새로운 모델을 추가하려면 환경 변수 설정과 백엔드 서버 재시작이 필요할 수 있습니다.
          </Typography>
          
          <Tooltip title="현재 Ollama와 OpenAI 모델만 지원됩니다. 다른 모델을 추가하려면 관리자에게 문의하세요.">
            <IconButton>
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
};

export default ModelsPage; 